/** eslint-disable react/react-in-jsx-scope -- Unaware of jsxImportSource */
/** @jsxImportSource @emotion/react */

import "../assets/scss/public-main.scss";
import styles from "../assets/scss/modules/PublicListings.module.scss";

import React, {useEffect, useState} from 'react';
import {useWpContext} from "../components/context/WordPressProvider.tsx";
import {Chip, css, CssBaseline, Divider, Skeleton, ToggleButton, ToggleButtonGroup} from "@mui/material";
import useCtrGridListings from "../hooks/useCtrGridListings.ts";
import {CtrInscriptions, Maybe, PropertyHit} from "../graphql/generated/graphql-main.tsx";
import useCtrMetaFields from "../hooks/useCtrMetaFields.tsx";
import useCtrGenresProprietes from "../hooks/useCtrGenresProprietes.ts";
import useCtrRegions from "../hooks/useCtrRegions.ts";
import useCtrValeursFixes from "../hooks/useCtrValeursFixes.ts";
import clsx from "clsx";
import {empty} from "../utils/utils.ts";
import FiltersRegion from "../components/views-public/Filters.Region.tsx";
import FiltersGenre from "../components/views-public/Filters.Genre.tsx";
import FiltersPriceSlider from "../components/views-public/Filters.PriceSlider.tsx";
import Button from "@mui/material/Button";
import {RestartAlt} from "@mui/icons-material";
import PropertiesGridView from "../components/views-public/Properties.GridView.tsx";
import {cfg} from "../config.ts";
import PropertiesMapView from "../components/views-public/Properties.MapView.tsx";
import Zircon from "../components/global/Zircon.tsx";
import {IconGrid, IconMap} from "../assets/icons/components";
import FiltersSearchAdvanced from "../components/views-public/Filters.SearchAdvanced.tsx";
import useReactTexts from "../hooks/useReactTexts.ts";

enum ViewMode {
    GRID = "grid",
    MAP = "map"
}

type Props = {}
const PublicListings = (props: Props) => {
    const {others} = useWpContext<ZListings>();
    const {__} = useReactTexts();
    const {$listings, loading: loadingListings} = useCtrGridListings();
    const {loading: loadingFixed} = useCtrValeursFixes();
    const {loading: loadingMetas} = useCtrMetaFields();
    const {loading: loadingGenres} = useCtrGenresProprietes(); // needed by useCtrInscription
    const {loading: loadingRegions} = useCtrRegions({deferUntilListings: true});

    const isLoading = loadingListings || loadingMetas || loadingFixed || loadingGenres || loadingRegions;

    /** local state (filters) */
    const [view, setView] = useState<ViewMode>(ViewMode.GRID);
    const [searchHits, setSearchHits] = useState<PropertyHit[] | null>(null);
    const [municipalities, setMunicipalities] = useState<string[]>([]);
    const [genres, setGenres] = useState<string[]>([]);
    const [minPrice, setMinPrice] = useState<number | null>(null);
    const [maxPrice, setMaxPrice] = useState<number | null>(null);
    const [reset, setReset] = useState<number>(0); // timestamp flag to trigger filters reset

    // whether any filter is active (used to toggle the 'reset filters' button)
    const hasActiveFilters = !empty(municipalities) || !empty(genres) || minPrice != null || maxPrice != null;

    // automatically filter all listings from filters or return entire dataset if no filters are set
    const $filteredListings: CtrInscriptions[] = $listings.filter(it => {
        let conditions: boolean[] = [];
        if (!empty(municipalities)) conditions.push(municipalities.includes(it.MUN_CODE ?? ""));
        if (!empty(genres)) conditions.push(genres.includes(it.GENRE_PROPRIETE ?? ""));
        // price comparison
        const _price = parseFloat(it.PRIX_DEMANDE || "0");
        if (minPrice != null) conditions.push(_price >= minPrice);
        if (maxPrice != null) conditions.push(_price <= maxPrice);
        return conditions.every(it => it);
    });

    // search results dependent on filtered listings -- OR relationship (null = no search term)
    const $searchedListings: Maybe<CtrInscriptions[]> = searchHits == null ? null : searchHits
        ?.map(hit => $filteredListings.find(it => it.NO_INSCRIPTION == hit.mls_id) ?? null)
        ?.filter(it => it != null) as CtrInscriptions[];

    // do NOT show filtered listings if no search results; we must show emptiness to user
    const $finalListings = $searchedListings ?? $filteredListings;

    const perPage = others.per_page ?? cfg.react.grid_view.listings_per_page;
    const showSearchBar = others.search && $listings.length > perPage;
    const showFilters = others.filter && $listings.length > perPage;
    const showCounter = others.counter;
    const showMaps = others.gmaps;

    // todo - test: either use SCSS .module.scss files or this @emotion
    const chipStyle = css`
        background-color: #faf8f1;
    `

    const renderLoadingSkeletons = () => {
        return <div className={"w-full"}>
            <Skeleton variant={"rectangular"} height={50} className={"w-full"}/>

            <div className={"mt-4 grid grid-cols-3 gap-4 w-full"}>
                {[...Array(15)].map((_, i) => (
                    <Skeleton
                        key={`skeleton-card-${i}`}
                        variant={"rectangular"}
                        className={"h-[250px] lg:h-[400px]"}
                    ></Skeleton>
                ))}
            </div>
        </div>;
    }

    const renderListingsCount = () => {
        const lbl = $finalListings.length > 1 ? __("propriétés") : __("propriété");
        return <Divider className={"mx-8 my-4"}>
            <Chip
                className={styles.chip__count}
                // css={chipStyle}
                size={"small"}
                label={<span className={"lowercase"}>{$finalListings.length} {lbl}</span>}
            ></Chip>
        </Divider>
    }

    // note: only render when corresponding option is checked in Elementor widget
    const renderSearchBar = () => {
        return <section className={"searchbar-embedded"}>
            <FiltersSearchAdvanced
                listings={$listings}
                onSearch={(term, hits) => setSearchHits(hits)}
            ></FiltersSearchAdvanced>

            {/* todo remove old search bar w/o autocomplete */}
            {/*<FiltersSearchBasic*/}
            {/*    listings={$listings}*/}
            {/*    onSearch={hits => setSearchHits(hits)}*/}
            {/*></FiltersSearchBasic>*/}
            {/*<Divider className={"my-4"}/>*/}
        </section>
    }

    const renderViewToggle = () => {
        return <ToggleButtonGroup
            className={clsx("view-toggler", styles.toggle_views)}
            value={view} exclusive
            onChange={(event, value) => {
                // when clicking multiple times on same view button, it returns null since there's no real change
                if (value == null) return;
                setView(value);
            }}
        >
            <ToggleButton value={ViewMode.GRID}>
                <Zircon component={IconGrid}/>
            </ToggleButton>
            <ToggleButton value={ViewMode.MAP}>
                <Zircon component={IconMap}/>
            </ToggleButton>
        </ToggleButtonGroup>
    }

    // show 'No listings' message when no listings are available based on search criteria
    const renderNoResultsMessage = () => {
        return <div className={"msg-no-listings col-span-full text-center my-8"}>
            {__("Désolé, aucun résultat ne correspond à votre recherche.")}
        </div>
    }

    // auto-hide Elementor nodes matching a specific class if there are NO listings available
    useEffect(() => {
        const displayMode = empty($listings) ? "none" : "revert";
        document.querySelectorAll(cfg.elementor.classes.hide_if_empty)
            .forEach(it => (it as HTMLElement).style.display = displayMode);
    }, [$listings]);

    // show skeleton while everything is loading
    if (isLoading) return renderLoadingSkeletons();

    return <React.Fragment>
        <CssBaseline/>

        {/* note: previously the search bar was here above filters */}

        {/* only show filters if 'filters' is ON in Elementor widget */}
        {showFilters && <div className={"mb-12 space-y-4"}>
            <section className={"filters grid sm:grid-cols-2 xl:grid-cols-4 gap-4"}>
                <FiltersRegion
                    listings={$listings}
                    onFilter={muns => setMunicipalities(muns)}
                    resetFlag={reset}
                ></FiltersRegion>

                <FiltersGenre
                    listings={$listings}
                    onFilter={genres => setGenres(genres)}
                    resetFlag={reset}
                ></FiltersGenre>

                <FiltersPriceSlider
                    listings={$listings}
                    onSetRange={(min, max) => {
                        setMinPrice(min);
                        setMaxPrice(max);
                    }}
                    resetFlag={reset}
                ></FiltersPriceSlider>

                {/* @deprecated: previous list of price options (no longer needed since using price slider) */}
                {/*<GridPriceFilter*/}
                {/*    listings={$listings}*/}
                {/*    onFilter={price => {*/}
                {/*        setPriceRange(price);*/}
                {/*    }}*/}
                {/*></GridPriceFilter>*/}
            </section>

            {/* button to clear filters (only show if active filters) */}
            {hasActiveFilters && <div className={"flex justify-end"}>
                <Button
                    className={clsx("!w-auto !justify-center", styles.btn_clear_filters)}
                    variant={"outlined"} startIcon={<RestartAlt/>}
                    onClick={() => setReset(Date.now())}
                ></Button>
            </div>}
        </div>}

        {showCounter && renderListingsCount()}

        {/* note: checking $listings.length prevents double-loading in component due to hook useCtrGridListings */}
        {/* show 'grid' view by default */}
        {(view == ViewMode.GRID && $listings.length > 0) && (
            <PropertiesGridView
                listings={$finalListings}
                hasSearchResults={$searchedListings != null}
                searchBar={showSearchBar ? renderSearchBar() : null}
                toggleGroup={showMaps ? renderViewToggle() : null}
                msgNoResults={empty($finalListings) ? renderNoResultsMessage() : null}
            ></PropertiesGridView>
        )}

        {(view == ViewMode.MAP && $listings.length > 0) && (
            <PropertiesMapView
                listings={$finalListings}
                searchBar={showSearchBar ? renderSearchBar() : null}
                toggleGroup={showMaps ? renderViewToggle() : null}
                msgNoResults={empty($finalListings) ? renderNoResultsMessage() : null}
            ></PropertiesMapView>
        )}

        {showCounter && renderListingsCount()}

    </React.Fragment>
}
export default PublicListings;
